/* ============================================================
   What a contact enquiry is.

   Imported by both the form and the route that receives it, so
   there is exactly one definition of "valid" on this site. The
   browser checks it to be helpful; the server checks it because
   anything can POST to /api/contact and the browser's opinion is
   not evidence.

   Nothing server-only may be imported here: this file ends up in
   the bundle.
   ============================================================ */

import { z } from 'zod';

export const ROUTES = [
  { id: 'operator', label: 'Warehouse operator', subject: 'Floor assessment' },
  { id: 'manufacturer', label: 'Robot manufacturer', subject: 'Fleet integration' },
  { id: 'wms', label: 'WMS vendor', subject: 'WMS integration' },
] as const;

const ROUTE_IDS = ROUTES.map((route) => route.id) as [string, ...string[]];

/* Upper bounds are not about tidiness. Every one of these ends up in an email
   body, and an unbounded field is an unbounded email. */
export const enquirySchema = z.object({
  route: z.enum(ROUTE_IDS, { message: 'Pick the one that fits you best.' }),

  name: z
    .string()
    .trim()
    .min(1, 'We need something to call you.')
    .max(120, 'That is longer than we can store.'),

  email: z
    .string()
    .trim()
    .min(1, 'We need somewhere to reply.')
    .max(254, 'That address is longer than an address can be.')
    .pipe(z.email('That address is missing something.')),

  company: z.string().trim().max(160, 'That is longer than we can store.').default(''),

  message: z
    .string()
    .trim()
    .min(10, 'A sentence or two is plenty.')
    .max(4000, 'That is more than an email will carry. Send the shape of it and we will ask.'),

  /** The honeypot. A real person never sees this field, so a real person never
   *  fills it.
   *
   *  Deliberately permissive: making it `.max(0)` meant a filled trap failed
   *  validation, and the 422 came back naming the field and quoting the rule.
   *  That is a free map of the trap. It parses, and the route decides. */
  company_url: z.string().max(200).optional().default(''),
});

export type Enquiry = z.infer<typeof enquirySchema>;
export type EnquiryField = keyof Enquiry;

/** One message per field, which is all a form can show against an input. */
export function fieldErrors(error: z.ZodError<Enquiry>) {
  const flat = z.flattenError(error).fieldErrors as Partial<Record<EnquiryField, string[]>>;
  const first: Partial<Record<EnquiryField, string>> = {};
  for (const [field, messages] of Object.entries(flat)) {
    if (messages?.[0]) first[field as EnquiryField] = messages[0];
  }
  return first;
}

export const routeFor = (id: string) => ROUTES.find((route) => route.id === id);
